// @ts-nocheck
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../../config/api';
import './HabitTracker.css';
import TaskModal from './TaskModal';

function toISODateLocal(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
}

function addDaysLocal(dateStr, days) {
    const [y, m, d] = dateStr.split('-').map(Number);
    const dt = new Date(y, m - 1, d);
    dt.setDate(dt.getDate() + days);
    return toISODateLocal(dt);
}

function monthShort(date) {
    return date.toLocaleDateString(undefined, { month: 'short' });
}

function addDateDays(date, days) {
    const next = new Date(date);
    next.setDate(next.getDate() + days);
    return next;
}

function startOfWeekMonday(date) {
    const d = new Date(date);
    const day = d.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    d.setDate(d.getDate() + diff);
    d.setHours(0, 0, 0, 0);
    return d;
}

function parseJsonSafe(text) {
    if (!text) return null;
    try {
        return JSON.parse(text);
    } catch (_e) {
        return null;
    }
}

const VIEW_ALL = 'all';
const VIEW_TODAY = 'today';
const VIEW_CALENDAR = 'calendar';
const VIEW_COMPLETED = 'completed';

function HabitTracker() {
    const navigate = useNavigate();
    const token = useMemo(() => localStorage.getItem('token') || sessionStorage.getItem('token'), []);

    const [lists, setLists] = useState([]);
    const [habits, setHabits] = useState([]);
    const [completionMap, setCompletionMap] = useState({});
    const [selectedView, setSelectedView] = useState(VIEW_ALL);
    const [selectedListId, setSelectedListId] = useState('');
    const [selectedHabitId, setSelectedHabitId] = useState('');
    const [statsByHabit, setStatsByHabit] = useState({});
    const [yearCompletionSet, setYearCompletionSet] = useState(new Set());
    const [loading, setLoading] = useState(true);
    const [mutating, setMutating] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [cellInFlight, setCellInFlight] = useState({});
    const [tasks, setTasks] = useState([]);
    const [showTaskModal, setShowTaskModal] = useState(false);
    const [editingTask, setEditingTask] = useState(null);

    const [showListModal, setShowListModal] = useState(false);
    const [showHabitModal, setShowHabitModal] = useState(false);
    const [editingHabit, setEditingHabit] = useState(null);
    const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
    const [isRightPanelOpen, setIsRightPanelOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [sortAscending, setSortAscending] = useState(true);
    const [showCompletedOnly, setShowCompletedOnly] = useState(false);

    const [listName, setListName] = useState('');
    const [habitName, setHabitName] = useState('');
    const [habitColor, setHabitColor] = useState('#22c55e');
    const [habitActive, setHabitActive] = useState(true);
    const [calendarPeriod, setCalendarPeriod] = useState('week'); // week, month, year
    const [expandedTaskId, setExpandedTaskId] = useState(null);

    const controllersRef = useRef([]);

    const today = useMemo(() => toISODateLocal(new Date()), []);
    const next7Days = useMemo(() => Array.from({ length: 7 }, (_, idx) => addDaysLocal(today, idx)), [today]);

    const request = useCallback(async (url, options = {}, redirectOn401 = true) => {
        const controller = new AbortController();
        controllersRef.current.push(controller);

        const headers = {
            'Content-Type': 'application/json',
            ...(options.headers || {}),
        };
        if (token) {
            headers.Authorization = `Bearer ${token}`;
        }

        try {
            const response = await fetch(url, {
                ...options,
                headers,
                signal: controller.signal,
            });

            if (response.status === 401 || response.status === 403) {
                if (redirectOn401) {
                    navigate('/signin', { state: { from: { pathname: '/app-store/habit-tracker' } } });
                }
                throw new Error('Unauthorized');
            }

            const text = await response.text();
            const json = parseJsonSafe(text);

            if (!response.ok) {
                if (response.status === 404 && String(url).includes('/habit-tracker/')) {
                    throw new Error('Habit Tracker API not found. Deploy latest backend on Render and restart the service.');
                }
                if (response.status >= 500) {
                    throw new Error('Habit Tracker server error. Check Render logs and restart backend.');
                }
                const msg = json?.error?.message || json?.message || 'Request failed';
                throw new Error(msg);
            }

            return json;
        } finally {
            controllersRef.current = controllersRef.current.filter((ctrl) => ctrl !== controller);
        }
    }, [navigate, token]);

    const heatmapScrollRef = useRef(null);
    const calendarScrollRef = useRef(null);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [globalYearCompletions, setGlobalYearCompletions] = useState({});
    const [selectedDateForCalendar, setSelectedDateForCalendar] = useState(new Date());

    const [heatmapLoading, setHeatmapLoading] = useState(false);

    const loadGlobalYearCompletions = useCallback(async (year) => {
        if (!year) return;
        setHeatmapLoading(true);
        const fromDate = `${year}-01-01`;
        const toDate = `${year}-12-31`;

        try {
            const res = await request(
                `${API_ENDPOINTS.HABIT_TRACKER.COMPLETIONS}?from=${encodeURIComponent(fromDate)}&to=${encodeURIComponent(toDate)}`
            );
            setGlobalYearCompletions(res?.data || {});
        } catch (_err) {
            setGlobalYearCompletions({});
        } finally {
            setHeatmapLoading(false);
        }
    }, [request]);

    const loadCore = useCallback(async () => {
        if (!token) {
            navigate('/signin', { state: { from: { pathname: '/app-store/habit-tracker' } } });
            return;
        }

        setLoading(true);
        setErrorMessage('');

        try {
            const from = next7Days[0];
            const to = next7Days[next7Days.length - 1];
            const [habitsRes, completionsRes, tasksRes] = await Promise.all([
                request(API_ENDPOINTS.HABIT_TRACKER.HABITS),
                request(`${API_ENDPOINTS.HABIT_TRACKER.COMPLETIONS}?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`),
                request(API_ENDPOINTS.HABIT_TRACKER.TASKS),
            ]);

            const nextHabits = habitsRes?.data || [];
            const nextCompletions = completionsRes?.data || {};
            const nextTasks = tasksRes?.data || [];

            setHabits(nextHabits);
            setCompletionMap(nextCompletions);
            setTasks(nextTasks);

            if (!selectedHabitId && nextHabits.length > 0) {
                setSelectedHabitId(String(nextHabits[0]._id));
            }
        } catch (err) {
            if (err && err.name === 'AbortError') return;
            setErrorMessage(err.message || 'Unable to load habit tracker data');
        } finally {
            setLoading(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [navigate, next7Days, request, token]);

    useEffect(() => {
        if (!loading && heatmapScrollRef.current) {
            heatmapScrollRef.current.scrollLeft = heatmapScrollRef.current.scrollWidth;
        }
    }, [loading, globalYearCompletions]);

    const loadHabitStats = useCallback(async (habitId) => {
        if (!habitId) return;
        try {
            const statsRes = await request(`${API_ENDPOINTS.HABIT_TRACKER.STATS(habitId)}&today=${encodeURIComponent(today)}`);
            setStatsByHabit((prev) => ({ ...prev, [habitId]: statsRes?.data || {} }));
        } catch (_err) {
            // Non-critical panel data.
        }
    }, [request, today]);

    /* Handled by moving above */

    const loadYearCompletionsForSelectedHabit = useCallback(async () => {
        if (!selectedHabitId) {
            setYearCompletionSet(new Set());
            return;
        }
        const habitDates = globalYearCompletions[selectedHabitId] || {};
        setYearCompletionSet(new Set(Object.keys(habitDates).filter((dateKey) => habitDates[dateKey])));
    }, [globalYearCompletions, selectedHabitId]);

    useEffect(() => {
        loadCore();
        return () => {
            controllersRef.current.forEach((ctrl) => ctrl.abort());
            controllersRef.current = [];
        };
    }, [loadCore]);

    useEffect(() => {
        loadHabitStats(selectedHabitId);
        loadGlobalYearCompletions(selectedYear);
    }, [loadHabitStats, loadGlobalYearCompletions, selectedHabitId, selectedYear]);

    useEffect(() => {
        if (!successMessage) return;
        const timer = setTimeout(() => setSuccessMessage(''), 2000);
        return () => clearTimeout(timer);
    }, [successMessage]);

    useEffect(() => {
        if (!isMobileSidebarOpen) return;
        const handleResize = () => {
            if (window.innerWidth > 1100) {
                setIsMobileSidebarOpen(false);
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [isMobileSidebarOpen]);

    const habitsByList = useMemo(() => {
        const grouped = {};
        for (const habit of habits) {
            const key = String(habit.listId);
            if (!grouped[key]) grouped[key] = [];
            grouped[key].push(habit);
        }
        return grouped;
    }, [habits]);

    const activeHabits = useMemo(() => habits.filter((h) => h.active !== false), [habits]);
    const listHabits = useMemo(() => activeHabits.filter((h) => String(h.listId) === String(selectedListId)), [activeHabits, selectedListId]);
    const selectedHabit = useMemo(() => habits.find((h) => String(h._id) === String(selectedHabitId)) || null, [habits, selectedHabitId]);

    const isCompleted = useCallback((habitId, date) => {
        return Boolean(completionMap?.[habitId]?.[date]);
    }, [completionMap]);

    const openCreateHabit = () => {
        setEditingHabit(null);
        setHabitName('');
        setHabitColor('#22c55e');
        setHabitActive(true);
        setShowHabitModal(true);
    };

    const openEditHabit = (habit) => {
        setEditingHabit(habit);
        setHabitName(habit.name || '');
        setHabitColor(habit.color || '#22c55e');
        setHabitActive(habit.active !== false);
        setShowHabitModal(true);
    };



    const saveHabit = async (habitData = null) => {
        const passedData = (habitData && typeof habitData === 'object' && 'name' in habitData) ? habitData : null;
        
        const name = passedData ? passedData.name.trim() : habitName.trim();
        const color = passedData ? passedData.color : habitColor;
        const active = passedData ? passedData.active : habitActive;

        if (!name) return null;

        setMutating(true);
        setErrorMessage('');

        try {
            let result = null;
            if (editingHabit && !passedData) {
                const res = await request(API_ENDPOINTS.HABIT_TRACKER.HABIT(editingHabit._id), {
                    method: 'PUT',
                    body: JSON.stringify({ name, color, active }),
                });
                result = res?.data;
                if (result) {
                    setHabits((prev) => prev.map((habit) => String(habit._id) === String(editingHabit._id) ? { ...habit, ...result } : habit));
                    setSuccessMessage('Habit updated');
                }
            } else {
                const res = await request(API_ENDPOINTS.HABIT_TRACKER.HABITS, {
                    method: 'POST',
                    body: JSON.stringify({ name, color }),
                });
                result = res?.data;
                if (result) {
                    setHabits((prev) => [...prev, result]);
                    setSelectedHabitId(String(result._id));
                    setSuccessMessage('Habit created');
                }
            }
            setShowHabitModal(false);
            setEditingHabit(null);
            return result;
        } catch (err) {
            setErrorMessage(err.message || 'Failed to save habit');
            return null;
        } finally {
            setMutating(false);
        }
    };

    const openCreateTask = () => {
        setEditingTask(null);
        setShowTaskModal(true);
    };

    const saveTask = async (taskData) => {
        setMutating(true);
        setErrorMessage('');
        try {
            if (editingTask) {
                const res = await request(API_ENDPOINTS.HABIT_TRACKER.TASK(editingTask._id), {
                    method: 'PUT',
                    body: JSON.stringify(taskData),
                });
                if (res?.data) {
                    setTasks(prev => prev.map(t => t._id === editingTask._id ? res.data : t));
                    setSuccessMessage('Task updated');
                    setShowTaskModal(false);
                }
            } else {
                const res = await request(API_ENDPOINTS.HABIT_TRACKER.TASKS, {
                    method: 'POST',
                    body: JSON.stringify(taskData),
                });
                if (res?.data) {
                    setTasks(prev => [...prev, res.data]);
                    setSuccessMessage('Task created');
                    setShowTaskModal(false);
                }
            }
        } catch (err) {
            setErrorMessage(err.errorMessage || err.message || 'Failed to save task');
        } finally {
            setMutating(false);
        }
    };

    const deleteHabit = async (habitId) => {
        const ok = window.confirm('Delete this habit and all its completion history?');
        if (!ok) return;

        setMutating(true);
        setErrorMessage('');

        try {
            await request(API_ENDPOINTS.HABIT_TRACKER.HABIT(habitId), { method: 'DELETE' });
            setHabits((prev) => prev.filter((habit) => String(habit._id) !== String(habitId)));
            setCompletionMap((prev) => {
                const next = { ...prev };
                delete next[String(habitId)];
                return next;
            });
            if (String(selectedHabitId) === String(habitId)) {
                const nextHabit = habits.find((h) => String(h._id) !== String(habitId));
                setSelectedHabitId(nextHabit ? String(nextHabit._id) : '');
            }
            setSuccessMessage('Habit deleted');
        } catch (err) {
            setErrorMessage(err.message || 'Failed to delete habit');
        } finally {
            setMutating(false);
        }
    };

    const toggleTaskCompletion = async (task) => {
        const nextCompleted = !task.completed;
        const taskId = task._id;
        
        // Optimistic update
        setTasks(prev => prev.map(t => t._id === taskId ? { ...t, completed: nextCompleted } : t));

        try {
            await request(API_ENDPOINTS.HABIT_TRACKER.TASK(taskId), {
                method: 'PUT',
                body: JSON.stringify({ completed: nextCompleted })
            });
            setSuccessMessage(nextCompleted ? 'Task completed' : 'Task reopened');
            
            // If linked to a habit, toggle that habit too for the start date
            if (task.habitId && task.startDate) {
                await toggleCompletion(task.habitId, task.startDate);
            }
            // Refresh heatmap if not triggered by linked habit
            loadGlobalYearCompletions(selectedYear);
        } catch (err) {
            // Revert on error
            setTasks(prev => prev.map(t => t._id === taskId ? { ...t, completed: !nextCompleted } : t));
            setErrorMessage(err.message || 'Failed to update task completion');
        }
    };

    const toggleCompletion = async (habitId, date) => {
        const cellKey = `${habitId}:${date}`;
        if (cellInFlight[cellKey]) return;

        const prevCompleted = isCompleted(habitId, date);

        setCellInFlight((prev) => ({ ...prev, [cellKey]: true }));
        setCompletionMap((prev) => {
            const current = prev?.[habitId] || {};
            const nextHabitMap = { ...current };
            if (prevCompleted) {
                delete nextHabitMap[date];
            } else {
                nextHabitMap[date] = true;
            }
            return { ...prev, [habitId]: nextHabitMap };
        });

        try {
            const res = await request(API_ENDPOINTS.HABIT_TRACKER.TOGGLE_COMPLETION, {
                method: 'POST',
                body: JSON.stringify({ habitId, date, today }),
            });

            const completed = !!res?.data?.completed;
            setCompletionMap((prev) => {
                const current = prev?.[habitId] || {};
                const nextHabitMap = { ...current };
                if (completed) {
                    nextHabitMap[date] = true;
                } else {
                    delete nextHabitMap[date];
                }
                return { ...prev, [habitId]: nextHabitMap };
            });

            if (res?.data?.stats) {
                setStatsByHabit((prev) => ({ ...prev, [habitId]: res.data.stats }));
            }
            
            // Re-fetch global data to update the heatmap
            loadGlobalYearCompletions(selectedYear);
        } catch (err) {
            setCompletionMap((prev) => {
                const current = prev?.[habitId] || {};
                const nextHabitMap = { ...current };
                if (prevCompleted) {
                    nextHabitMap[date] = true;
                } else {
                    delete nextHabitMap[date];
                }
                return { ...prev, [habitId]: nextHabitMap };
            });
            setErrorMessage(err.message || 'Failed to toggle completion');
        } finally {
            setCellInFlight((prev) => {
                const next = { ...prev };
                delete next[cellKey];
                return next;
            });
        }
    };

    const selectedStats = statsByHabit[selectedHabitId] || { currentStreak: 0, longestStreak: 0, totalCompletions: 0 };
    const allTaskCount = activeHabits.length;
    const todayTaskCount = activeHabits.filter((habit) => isCompleted(String(habit._id), today)).length;
    const next7TaskCount = activeHabits.filter((habit) => next7Days.some((dateStr) => isCompleted(String(habit._id), dateStr))).length;
    const completedTaskCount = activeHabits.filter((habit) => isCompleted(String(habit._id), today)).length;
    const inboxTaskCount = activeHabits.filter((habit) => !isCompleted(String(habit._id), today)).length;
    const listNameById = useMemo(
        () => lists.reduce((acc, list) => ({ ...acc, [String(list._id)]: list.name }), {}),
        [lists]
    );

    const handleSelectView = (view, nextListId) => {
        setSelectedView(view);
        if (nextListId) setSelectedListId(String(nextListId));
        setIsMobileSidebarOpen(false);
    };

    const baseHabits = useMemo(() => {
        if (selectedView === VIEW_TODAY) return activeHabits;
        if (selectedView === VIEW_CALENDAR) return activeHabits;
        if (selectedView === VIEW_COMPLETED) return activeHabits;
        return activeHabits;
    }, [activeHabits, selectedView]);

    const normalizedSearch = searchQuery.trim().toLowerCase();

    const rowHabits = useMemo(() => {
        let next = [...baseHabits];

        // Filter out habits that already have tasks associated with them to prevent duplicates
        const habitIdsWithTasks = new Set(tasks.map(t => String(t.habitId)).filter(id => id && id !== 'null'));
        next = next.filter(h => !habitIdsWithTasks.has(String(h._id)));

        if (selectedView === VIEW_COMPLETED || showCompletedOnly) {
            next = next.filter((habit) => isCompleted(String(habit._id), today));
        }

        if (normalizedSearch) {
            next = next.filter((habit) => {
                return habit.name?.toLowerCase().includes(normalizedSearch);
            });
        }

        next.sort((a, b) => {
            const aName = String(a.name || '').toLowerCase();
            const bName = String(b.name || '').toLowerCase();
            if (sortAscending) return aName.localeCompare(bName);
            return bName.localeCompare(aName);
        });

        return next;
    }, [baseHabits, isCompleted, normalizedSearch, selectedView, showCompletedOnly, sortAscending, today, tasks]);

    const overdueHabits = rowHabits.filter((habit) => !isCompleted(String(habit._id), today));
    const viewTitle = selectedView === VIEW_TODAY
        ? 'Today'
        : selectedView === VIEW_CALENDAR
            ? 'Calendar'
            : selectedView === VIEW_COMPLETED
                ? 'Completed'
                : 'All';

    const renderSidebarContent = () => (
        <>
            <div className="habit-sidebar-top-icons">
                <button type="button" onClick={() => loadCore(false)} aria-label="Refresh data"><i className="fi fi-rr-refresh"></i></button>
                <button type="button" onClick={() => setSearchQuery('')} aria-label="Clear search"><i className="fi fi-rr-search"></i></button>
            </div>

            <div className="habit-sidebar-group">
                <button type="button" className={`habit-nav-item ${selectedView === VIEW_ALL ? 'is-active' : ''}`} onClick={() => handleSelectView(VIEW_ALL)}>
                    <span className="habit-nav-main">
                        <i className="fi fi-rr-layers"></i>
                        <span>All</span>
                    </span>
                    <small>{allTaskCount}</small>
                </button>
                <button type="button" className={`habit-nav-item ${selectedView === VIEW_TODAY ? 'is-active' : ''}`} onClick={() => handleSelectView(VIEW_TODAY)}>
                    <span className="habit-nav-main">
                        <i className="fi fi-rr-calendar-day"></i>
                        <span>Today</span>
                    </span>
                    <small>{todayTaskCount}</small>
                </button>
                <button type="button" className={`habit-nav-item ${selectedView === VIEW_CALENDAR ? 'is-active' : ''}`} onClick={() => handleSelectView(VIEW_CALENDAR)}>
                    <span className="habit-nav-main">
                        <i className="fi fi-rr-calendar"></i>
                        <span>Calendar</span>
                    </span>
                </button>
            </div>


            <div className="habit-sidebar-footer-links">
                <button type="button" onClick={() => handleSelectView(VIEW_COMPLETED)}><i className="fi fi-rr-check"></i><span>Completed</span></button>
            </div>

            <button type="button" className="habit-secondary" onClick={openCreateTask} disabled={mutating} aria-label="Add task">
                <i className="fi fi-rr-plus"></i>
                <span>Add Task</span>
            </button>
        </>
    );

    const heatmapModel = useMemo(() => {
        const startDate = new Date(Date.UTC(selectedYear, 0, 1));
        const endDate = new Date(Date.UTC(selectedYear, 11, 31));
        
        const startWeek = startOfWeekMonday(startDate);
        
        const weekColumns = [];
        const monthLabels = [];

        const globalCountByDate = {};
        
        Object.keys(globalYearCompletions).forEach(habitId => {
            const habitCompletions = globalYearCompletions[habitId] || {};
            Object.keys(habitCompletions).forEach(date => {
                if (habitCompletions[date]) {
                    globalCountByDate[date] = (globalCountByDate[date] || 0) + 1;
                }
            });
        });

        tasks.forEach(task => {
            if (task.completed && task.startDate) {
                if (!task.habitId) {
                    globalCountByDate[task.startDate] = (globalCountByDate[task.startDate] || 0) + 1;
                }
            }
        });

        const todayObj = new Date();
        todayObj.setHours(0, 0, 0, 0);

        for (let weekIndex = 0; weekIndex < 54; weekIndex += 1) {
            const weekStart = addDateDays(startWeek, weekIndex * 7);
            if (weekStart > endDate) break;

            const cells = [];
            for (let dayIndex = 0; dayIndex < 7; dayIndex += 1) {
                const date = addDateDays(weekStart, dayIndex);
                const iso = toISODateLocal(date);
                const isCurrentYear = date.getUTCFullYear() === selectedYear;
                const isFuture = date > todayObj;
                const count = (isCurrentYear && !isFuture) ? (globalCountByDate[iso] || 0) : 0;
                
                let level = 0;
                if (count > 0) {
                    if (count <= 1) level = 1;
                    else if (count <= 2) level = 2;
                    else if (count <= 4) level = 3;
                    else level = 4;
                }

                cells.push({ 
                    key: iso, 
                    iso, 
                    count, 
                    level, 
                    isFuture, 
                    isOtherYear: !isCurrentYear 
                });
            }

            if (weekIndex === 0 || (weekStart.getDate() <= 7 && weekStart.getUTCFullYear() === selectedYear)) {
                monthLabels.push({
                    key: `${weekStart.getFullYear()}-${weekStart.getMonth()}`,
                    label: monthShort(weekStart),
                    weekIndex,
                });
            }

            weekColumns.push({ weekIndex, cells });
        }

        return { weekColumns, monthLabels };
    }, [selectedYear, globalYearCompletions, tasks]);

    useEffect(() => {
        if (selectedView === VIEW_CALENDAR && calendarScrollRef.current) {
            const now = new Date();
            const hour = now.getHours();
            // Scroll to current hour minus 2 to give some context above the line
            const scrollPos = Math.max(0, (hour * 60) - 120);
            calendarScrollRef.current.scrollTop = scrollPos;
        }
    }, [selectedView]);

    const renderCalendar = () => {
        const hours = Array.from({ length: 24 }, (_, i) => i);
        const startDate = startOfWeekMonday(selectedDateForCalendar);
        const viewDays = Array.from({ length: 7 }, (_, i) => addDateDays(startDate, i));
        
        const now = new Date();
        const currentIsoDate = toISODateLocal(now);
        const currentHour = now.getHours();
        const currentMinute = now.getMinutes();

        const getTaskPosition = (startTime) => {
            if (!startTime) return 0;
            const [h, m] = startTime.split(':').map(Number);
            return (h * 60 + m);
        };

        const getTaskHeight = (startTime, endTime) => {
            if (!startTime || !endTime) return 60;
            const start = getTaskPosition(startTime);
            const end = getTaskPosition(endTime);
            return Math.max(end - start, 30);
        };

        return (
            <div className="habit-calendar-view v2">
                <header className="habit-calendar-v2-header">
                    <div className="h-cal-left">
                        <button className="h-cal-today-btn" onClick={() => setSelectedDateForCalendar(new Date())}>Today</button>
                        <div className="h-cal-nav">
                            <button onClick={() => setSelectedDateForCalendar(addDateDays(selectedDateForCalendar, -7))}><i className="fi fi-rr-angle-left"></i></button>
                            <button onClick={() => setSelectedDateForCalendar(addDateDays(selectedDateForCalendar, 7))}><i className="fi fi-rr-angle-right"></i></button>
                        </div>
                        <h2 className="h-cal-month-label">
                            {selectedDateForCalendar.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}
                        </h2>
                    </div>
                </header>

                <div className="h-cal-grid-container">
                    <div className="h-cal-days-header">
                        <div className="h-cal-time-gutter"></div>
                        {viewDays.map(d => {
                            const isToday = toISODateLocal(d) === currentIsoDate;
                            return (
                                <div key={d.toISOString()} className={`h-cal-day-label ${isToday ? 'is-today' : ''}`}>
                                    <span>{d.toLocaleDateString(undefined, { weekday: 'short' }).toUpperCase()}</span>
                                    <strong className="day-num">{d.getDate()}</strong>
                                </div>
                            );
                        })}
                    </div>

                    <div className="h-cal-scroll-area" ref={calendarScrollRef}>
                        <div className="h-cal-grid-content">
                            <div className="h-cal-time-gutter">
                                {hours.map(h => (
                                    <div key={h} className="h-cal-hour-label">
                                        {h === 0 ? '' : h > 12 ? `${h - 12} PM` : h === 12 ? '12 PM' : `${h} AM`}
                                    </div>
                                ))}
                            </div>
                            
                            <div className="h-cal-days-grid">
                                {hours.map(h => (
                                    <div key={h} className="h-cal-grid-row"></div>
                                ))}
                                
                                {viewDays.map(d => {
                                    const iso = toISODateLocal(d);
                                    const dayTasks = tasks.filter(t => t.startDate === iso);
                                    const isToday = iso === currentIsoDate;

                                    return (
                                        <div key={iso} className="h-cal-day-column">
                                            {dayTasks.map(task => {
                                                const top = getTaskPosition(task.startTime);
                                                const height = getTaskHeight(task.startTime, task.endTime);
                                                const habitColor = habits.find(h => h._id === task.habitId)?.color || '#3b82f6';
                                                
                                                return (
                                                    <div 
                                                        key={task._id} 
                                                        className="h-cal-task-block"
                                                        style={{ 
                                                            top: `${top}px`, 
                                                            height: `${height}px`,
                                                            backgroundColor: habitColor,
                                                            borderLeft: `4px solid ${habitColor}CC`
                                                        }}
                                                        onClick={() => { setEditingTask(task); setShowTaskModal(true); }}
                                                    >
                                                        <div className="h-cal-task-title">{task.title}</div>
                                                        <div className="h-cal-task-time">{task.startTime} - {task.endTime}</div>
                                                    </div>
                                                );
                                            })}
                                            {isToday && (
                                                <div 
                                                    className="h-cal-now-indicator" 
                                                    style={{ top: `${currentHour * 60 + currentMinute}px` }}
                                                >
                                                    <div className="indicator-dot"></div>
                                                    <div className="indicator-line"></div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    if (loading) {
        return (
            <div className="habit-page">
                <div className="habit-loading">Loading Habit Tracker...</div>
            </div>
        );
    }

    return (
        <>
            <Helmet>
                <title>Habit Tracker | Hari Vishnu</title>
                <meta name="description" content="Build habits with daily check-ins, streaks, and a 7-day view." />
                <meta name="robots" content="noindex,nofollow" />
            </Helmet>

            <div className="habit-page">
                {(errorMessage || successMessage) && (
                    <div className={`habit-banner ${errorMessage ? 'is-error' : 'is-success'}`} role="status">
                        {errorMessage || successMessage}
                    </div>
                )}

                <div className="habit-shell">
                    <aside className="habit-sidebar habit-sidebar-desktop">
                        {renderSidebarContent()}
                    </aside>

                    <div className={`habit-mobile-drawer-overlay sidebar-overlay ${isMobileSidebarOpen ? 'is-open' : ''}`} onClick={() => setIsMobileSidebarOpen(false)}></div>
                    <aside className={`habit-sidebar habit-sidebar-mobile ${isMobileSidebarOpen ? 'is-open' : ''}`} onClick={(e) => e.stopPropagation()}>
                        <div className="habit-mobile-drawer-head">
                            <strong>Menu</strong>
                            <button type="button" onClick={() => setIsMobileSidebarOpen(false)} aria-label="Close sidebar">
                                <i className="fi fi-rr-cross-small"></i>
                            </button>
                        </div>
                        {renderSidebarContent()}
                    </aside>

                    <main className={`habit-main ${selectedView === VIEW_CALENDAR ? 'is-calendar' : ''}`}>
                        {selectedView !== VIEW_CALENDAR && (
                            <>
                                <div className="habit-header-row">
                                    <button type="button" className="habit-mobile-menu-btn" onClick={() => setIsMobileSidebarOpen(true)} aria-label="Open sidebar">
                                        <i className="fi fi-rr-menu-burger"></i>
                                    </button>
                                    <div className="habit-header-title">
                                        <h2>{viewTitle}</h2>
                                    </div>
                                    <button type="button" className="habit-streak-toggle-btn" onClick={() => setIsRightPanelOpen(true)} aria-label="Habit Streak">
                                        <i className="fi fi-rr-chart-user"></i>
                                        <span>Stats</span>
                                    </button>
                                </div>

                                <div className="habit-main-top-tools">
                                    <div className="habit-main-actions-top">
                                        <input
                                            type="search"
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            className="habit-search"
                                            placeholder="Search tasks"
                                            aria-label="Search tasks"
                                        />
                                        <button type="button" className="habit-chip-btn" onClick={() => setSortAscending((prev) => !prev)} aria-label="Toggle sort order">
                                            <i className="fi fi-rr-sort-alt"></i>
                                            <span>{sortAscending ? 'A-Z' : 'Z-A'}</span>
                                        </button>
                                        <button type="button" className={`habit-chip-btn ${showCompletedOnly ? 'is-active' : ''}`} onClick={() => setShowCompletedOnly((prev) => !prev)} aria-label="Toggle completed only">
                                            <i className="fi fi-rr-check"></i>
                                            <span>Completed</span>
                                        </button>
                                    </div>
                                </div>

                                <button type="button" className="habit-add-task" onClick={openCreateTask}>
                                    <i className="fi fi-rr-plus"></i>
                                    <span>Add task to Inbox</span>
                                </button>
                            </>
                        )}

                        {selectedView === VIEW_CALENDAR ? (
                            renderCalendar()
                        ) : (
                            <>
                                {tasks.length > 0 && (
                                    <section className="habit-task-section">
                                        <div className="habit-task-section-head">
                                            <span>Upcoming Tasks</span>
                                            <small>{tasks.length}</small>
                                        </div>
                                        {tasks.map((task) => (
                                            <article key={task._id} className={`habit-task-card is-expandable ${task.completed ? 'is-completed' : ''} ${expandedTaskId === task._id ? 'is-expanded' : ''}`} onClick={() => setExpandedTaskId(expandedTaskId === task._id ? null : task._id)}>
                                                <div 
                                                    className={`habit-task-checkbox ${task.completed ? 'is-checked' : ''}`} 
                                                    onClick={(e) => { 
                                                        e.stopPropagation(); 
                                                        toggleTaskCompletion(task);
                                                    }}
                                                >
                                                    {task.completed ? <i className="fi fi-rr-check"></i> : <i className="fi fi-rr-circle"></i>}
                                                </div>
                                                <div className="habit-task-content">
                                                    <div className="habit-task-title-row">
                                                        <h4>{task.title}</h4>
                                                        <i className={`fi fi-rr-angle-small-${expandedTaskId === task._id ? 'up' : 'down'} expanded-icon`}></i>
                                                    </div>
                                                    <p>
                                                        {task.startDate} {task.startTime} 
                                                        {task.habitId && ` • ${habits.find(h => h._id === task.habitId)?.name || ''}`}
                                                    </p>
                                                </div>
                                                
                                                {expandedTaskId === task._id && (
                                                    <div className="habit-task-details" onClick={(e) => e.stopPropagation()}>
                                                        <div className="habit-task-detail-block" dangerouslySetInnerHTML={{ __html: task.description || 'No description' }}></div>
                                                        <div className="habit-task-detail-meta">
                                                            <span><i className="fi fi-rr-flag"></i> {task.priority}</span>
                                                            <span><i className="fi fi-rr-calendar"></i> Ends: {task.endDate} {task.endTime}</span>
                                                        </div>
                                                        <div className="habit-task-detail-actions">
                                                            <button type="button" className="habit-btn-outline" onClick={() => { setEditingTask(task); setShowTaskModal(true); }}>Edit Task</button>
                                                            <button type="button" className="habit-btn-outline is-danger" onClick={async () => {
                                                                if(window.confirm('Delete this task?')) {
                                                                    await request(API_ENDPOINTS.HABIT_TRACKER.TASK(task._id), { method: 'DELETE' });
                                                                    setTasks(prev => prev.filter(t => t._id !== task._id));
                                                                }
                                                            }}>Delete</button>
                                                        </div>
                                                    </div>
                                                )}

                                                {!expandedTaskId && (
                                                    <div className="habit-task-actions">
                                                        <button type="button" onClick={async (e) => { 
                                                            e.stopPropagation(); 
                                                            if(window.confirm('Delete this task?')) {
                                                                await request(API_ENDPOINTS.HABIT_TRACKER.TASK(task._id), { method: 'DELETE' });
                                                                setTasks(prev => prev.filter(t => t._id !== task._id));
                                                            }
                                                        }}>
                                                            <i className="fi fi-rr-trash"></i>
                                                        </button>
                                                    </div>
                                                )}
                                            </article>
                                        ))}
                                    </section>
                                )}

                                <section className="habit-task-section">
                                    <div className="habit-task-section-head">
                                        <span>Standalone Habits</span>
                                        <small>{rowHabits.length}</small>
                                    </div>

                                    {rowHabits.length === 0 ? (
                                        <div className="habit-empty">No habits yet. Create your first habit to get started.</div>
                                    ) : (
                                        rowHabits.map((habit) => {
                                            const habitId = String(habit._id);
                                            const checked = isCompleted(habitId, today);
                                            const key = `${habitId}:${today}`;
                                            const pending = !!cellInFlight[key];

                                            return (
                                                <article key={habitId} className="habit-task-card" onClick={() => setSelectedHabitId(habitId)}>
                                                    <button
                                                        type="button"
                                                        className={`habit-task-checkbox ${checked ? 'is-checked' : ''}`}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            toggleCompletion(habitId, today);
                                                        }}
                                                        disabled={pending}
                                                    >
                                                        {pending ? '...' : checked ? '✓' : ''}
                                                    </button>

                                                    <div className="habit-task-content">
                                                        <h4>{habit.name}</h4>
                                                    </div>

                                                    <div className="habit-task-actions">
                                                        <button type="button" onClick={(e) => { e.stopPropagation(); openEditHabit(habit); }}>
                                                            <i className="fi fi-rr-edit"></i>
                                                        </button>
                                                        <button type="button" onClick={(e) => { e.stopPropagation(); deleteHabit(habitId); }}>
                                                            <i className="fi fi-rr-trash"></i>
                                                        </button>
                                                    </div>
                                                </article>
                                            );
                                        })
                                    )}
                                </section>
                            </>
                        )}
                    </main>

                    <div className={`habit-mobile-drawer-overlay streak-overlay ${isRightPanelOpen ? 'is-open' : ''}`} onClick={() => setIsRightPanelOpen(false)}></div>
                    <aside className={`habit-right-panel ${isRightPanelOpen ? 'is-open' : ''}`}>
                        {isRightPanelOpen && (
                            <div className="habit-mobile-drawer-head">
                                <strong>Habit Streak</strong>
                                <button type="button" onClick={() => setIsRightPanelOpen(false)} aria-label="Close streak panel">
                                    <i className="fi fi-rr-cross-small"></i>
                                </button>
                            </div>
                        )}
                        <div className="habit-right-panel-top" aria-hidden="true">
                            <i className="fi fi-rr-checkbox"></i>
                            <span>Due Date</span>
                        </div>
                        <h3>Habit Details</h3>
                        {selectedHabit ? (
                            <>
                                <div className="habit-right-title">
                                    <span className="habit-dot" style={{ backgroundColor: selectedHabit.color || '#22c55e' }}></span>
                                    <strong>{selectedHabit.name}</strong>
                                </div>

                                <div className="habit-stats">
                                    <div>
                                        <small>Current Streak</small>
                                        <strong>{selectedStats.currentStreak || 0}</strong>
                                    </div>
                                    <div>
                                        <small>Longest Streak</small>
                                        <strong>{selectedStats.longestStreak || 0}</strong>
                                    </div>
                                    <div>
                                        <small>Total Completions</small>
                                        <strong>{selectedStats.totalCompletions || 0}</strong>
                                    </div>
                                </div>

                                <div className="habit-note-block">
                                    <h4>Details:</h4>
                                    <ol>
                                        <li>Completed today: {isCompleted(String(selectedHabit._id), today) ? 'Yes' : 'No'}</li>
                                    </ol>
                                </div>

                                <div className={`habit-calendar ${heatmapLoading ? 'is-loading' : ''}`}>
                                   <div className="habit-heatmap-header">
                                       <div className="habit-year-nav">
                                           <button type="button" onClick={(e) => { e.preventDefault(); setSelectedYear(prev => prev - 1); }} aria-label="Previous year">
                                               <i className="fi fi-rr-angle-left"></i>
                                           </button>
                                           <strong>{selectedYear}</strong>
                                           <button type="button" onClick={(e) => { e.preventDefault(); setSelectedYear(prev => prev + 1); }} aria-label="Next year">
                                               <i className="fi fi-rr-angle-right"></i>
                                           </button>
                                       </div>
                                       <div className="habit-heatmap-legend" aria-hidden="true">
                                           <span>Less</span>
                                           <i className="level-0"></i>
                                           <i className="level-1"></i>
                                           <i className="level-2"></i>
                                           <i className="level-3"></i>
                                           <i className="level-4"></i>
                                           <span>More</span>
                                       </div>
                                   </div>
                                   
                                   <div className="habit-heatmap-wrap">
                                        {/* Simple Skeleton Overlay */}
                                        {heatmapLoading && <div className="habit-heatmap-skeleton"></div>}
                                        
                                        <div className="habit-heatmap-scroll" ref={heatmapScrollRef}>
                                            <div className="habit-heatmap-months" aria-hidden="true">
                                                {heatmapModel.monthLabels.map((monthLabel) => (
                                                    <span key={monthLabel.key} style={{ gridColumn: `${monthLabel.weekIndex + 1} / span 4` }}>
                                                        {monthLabel.label}
                                                    </span>
                                                ))}
                                            </div>

                                            <div className="habit-heatmap-area">
                                                <div className="habit-heatmap-weekdays" aria-hidden="true">
                                                    <span>Mon</span>
                                                    <span></span>
                                                    <span>Wed</span>
                                                    <span></span>
                                                    <span>Fri</span>
                                                    <span></span>
                                                    <span></span>
                                                </div>

                                                <div className="habit-heatmap-grid" role="img" aria-label={`Task completion heatmap for ${selectedYear}`}>
                                                    {heatmapModel.weekColumns.map((week) => (
                                                        <div key={week.weekIndex} className="habit-heatmap-week">
                                                            {week.cells.map((cell) => (
                                                                <span
                                                                    key={cell.key}
                                                                    className={`habit-heatmap-cell level-${cell.level} ${cell.isFuture ? 'is-future' : ''} ${cell.isOtherYear ? 'is-hidden' : ''}`}
                                                                    title={`${cell.iso}${cell.count > 0 ? ` - ${cell.count} completions` : ' - No completions'}`}
                                                                ></span>
                                                            ))}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <div className="habit-right-empty">Select a habit to see streak details.</div>
                        )}
                    </aside>
                </div>



                {showHabitModal && (
                    <div className="habit-modal-overlay" onClick={() => setShowHabitModal(false)}>
                        <div className="habit-modal" onClick={(e) => e.stopPropagation()}>
                            <h3>{editingHabit ? 'Edit Habit' : 'New Habit'}</h3>
                            <input
                                type="text"
                                value={habitName}
                                onChange={(e) => setHabitName(e.target.value)}
                                placeholder="Habit name"
                                maxLength={80}
                                aria-label="Habit name"
                            />

                            <div className="habit-color-row">
                                <label htmlFor="habitColor">Color</label>
                                <input id="habitColor" type="color" value={habitColor} onChange={(e) => setHabitColor(e.target.value)} aria-label="Habit color" />
                            </div>
                            {editingHabit && (
                                <label className="habit-active-toggle">
                                    <input type="checkbox" checked={habitActive} onChange={(e) => setHabitActive(e.target.checked)} />
                                    <span>Active</span>
                                </label>
                            )}
                            <div className="habit-modal-actions">
                                <button type="button" onClick={() => setShowHabitModal(false)}>Cancel</button>
                                <button type="button" onClick={saveHabit} disabled={mutating || !habitName.trim()}>
                                    {editingHabit ? 'Save' : 'Create'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                <TaskModal 
                    isOpen={showTaskModal}
                    onClose={() => setShowTaskModal(false)}
                    onSave={saveTask}
                    habits={habits}
                    editingTask={editingTask}
                    lists={lists}
                    onSaveHabit={saveHabit}
                />
            </div>
        </>
    );
}

export default HabitTracker;
